/**
 * useSearchCategories Hook
 *
 * React hook for searching categories
 */

'use client';

import { useState, useCallback } from 'react';
import { searchCategories } from '../../api';
import type { CategorySearchResponse } from '../../types';

export interface UseSearchCategoriesReturn {
  results: CategorySearchResponse | null;
  loading: boolean;
  error: string | null;
  search: (query: string) => Promise<void>;
  reset: () => void;
}

/**
 * Hook for searching categories with loading and error states
 */
export function useSearchCategories(): UseSearchCategoriesReturn {
  const [results, setResults] = useState<CategorySearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (query: string) => {
    if (!query.trim()) {
      setResults(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await searchCategories({ search: query });
      setResults(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResults(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    results,
    loading,
    error,
    search,
    reset,
  };
}
